import React, { useEffect, useRef, useState } from 'react';
import {
  Viewer as SpeckleViewer,
  DefaultViewerParams,
  DefaultLightConfiguration,
  SpeckleLoader,
  UrlHelper,
  CameraController,
  SelectionExtension,
  SectionTool,
  SectionOutlines,
  MeasurementsExtension,
} from '@speckle/viewer';

/** Soft architectural lighting — Speckle stock light config (docs/SPECKLE_VIEWER.md) */
const R1AS_LIGHT_CONFIG = {
  ...DefaultLightConfiguration,
  enabled: true,
  castShadow: true,
  shadowcatcher: true,
  intensity: 4.2,
  indirectLightIntensity: 1.15,
  elevation: 1.25,
  azimuth: 0.9,
};

/**
 * Speckle 3D viewer (Speckle-native controls only — no R3F/drei).
 * @param {{ resourceUrl?: string, authToken?: string, className?: string, heightClass?: string, enableSelection?: boolean, showTools?: boolean }} props
 * See docs/SPECKLE_VIEWER.md
 */
function Viewer({
  resourceUrl,
  authToken,
  className = '',
  heightClass = 'h-[min(80vh,720px)]',
  enableSelection = true,
  showTools = true,
}) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const sectionRef = useRef(null);
  const measureRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [sectionOn, setSectionOn] = useState(false);
  const [measureOn, setMeasureOn] = useState(false);

  const resolvedUrl =
    resourceUrl ||
    process.env.REACT_APP_SPECKLE_R1AS_URL ||
    '';
  const resolvedToken =
    authToken ||
    process.env.REACT_APP_SPECKLE_TOKEN ||
    '';

  useEffect(() => {
    let cancelled = false;
    let resizeObserver;

    const setupViewer = async () => {
      const container = containerRef.current;
      if (!container) return;

      if (!resolvedUrl) {
        setStatus('error');
        setError(
          'No Speckle model URL. Set REACT_APP_SPECKLE_R1AS_URL or pass ?url= on /viewer.'
        );
        return;
      }

      setStatus('loading');
      setError(null);
      setSectionOn(false);
      setMeasureOn(false);

      try {
        if (viewerRef.current) {
          viewerRef.current.dispose();
          viewerRef.current = null;
          sectionRef.current = null;
          measureRef.current = null;
          container.replaceChildren();
        }

        const params = {
          ...DefaultViewerParams,
          showStats: false,
          verbose: false,
          restrictInputToCanvas: true,
        };
        const viewer = new SpeckleViewer(container, params);
        await viewer.init();
        if (cancelled) {
          viewer.dispose();
          return;
        }

        viewer.createExtension(CameraController);
        if (enableSelection) {
          viewer.createExtension(SelectionExtension);
        }
        const sections = viewer.createExtension(SectionTool);
        viewer.createExtension(SectionOutlines);
        const measurements = viewer.createExtension(MeasurementsExtension);
        measurements.enabled = false;
        sections.enabled = false;

        viewer.setLightConfiguration(R1AS_LIGHT_CONFIG);

        viewerRef.current = viewer;
        sectionRef.current = sections;
        measureRef.current = measurements;

        resizeObserver = new ResizeObserver(() => {
          viewer.resize();
        });
        resizeObserver.observe(container);

        const urls = await UrlHelper.getResourceUrls(
          resolvedUrl,
          resolvedToken || undefined
        );
        if (!urls?.length) {
          throw new Error(`UrlHelper returned no resources for: ${resolvedUrl}`);
        }

        for (const url of urls) {
          if (cancelled) return;
          const loader = new SpeckleLoader(
            viewer.getWorldTree(),
            url,
            resolvedToken || ''
          );
          await viewer.loadObject(loader, true);
        }

        viewer.setLightConfiguration(R1AS_LIGHT_CONFIG);
        viewer.resize();

        if (!cancelled) setStatus('ready');
      } catch (err) {
        console.error('Speckle viewer failed to load', err);
        if (!cancelled) {
          setStatus('error');
          setError(err?.message || String(err));
        }
      }
    };

    setupViewer();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (viewerRef.current) {
        viewerRef.current.dispose();
        viewerRef.current = null;
      }
      sectionRef.current = null;
      measureRef.current = null;
    };
  }, [resolvedUrl, resolvedToken, enableSelection]);

  const toggleSection = () => {
    const sections = sectionRef.current;
    if (!sections || status !== 'ready') return;
    sections.toggle();
    setSectionOn((prev) => !prev);
    if (measureOn && measureRef.current) {
      measureRef.current.enabled = false;
      setMeasureOn(false);
    }
  };

  const toggleMeasure = () => {
    const measurements = measureRef.current;
    if (!measurements || status !== 'ready') return;
    const next = !measureOn;
    measurements.enabled = next;
    setMeasureOn(next);
    if (next && sectionOn && sectionRef.current) {
      sectionRef.current.toggle();
      setSectionOn(false);
    }
  };

  const toolBtn =
    'px-3 py-1.5 text-sm rounded-[10px] border border-warm-200 bg-white hover:bg-warm-50 disabled:opacity-40 disabled:pointer-events-none transition-colors';
  const toolBtnActive = 'bg-gray-900 text-white border-gray-900 hover:bg-gray-800 hover:text-white';

  return (
    <div className={`w-full ${className}`}>
      {status === 'loading' && (
        <p className="text-sm text-gray-500 mb-2 font-bai">Loading Speckle model…</p>
      )}
      {status === 'error' && (
        <p className="text-sm text-red-700 mb-2 font-bai" role="alert">
          {error}
        </p>
      )}

      {showTools && (
        <div className="flex flex-wrap gap-2 mb-3 font-bai">
          <button
            type="button"
            className={`${toolBtn} ${sectionOn ? toolBtnActive : ''}`}
            onClick={toggleSection}
            disabled={status !== 'ready'}
            aria-pressed={sectionOn}
          >
            Section
          </button>
          <button
            type="button"
            className={`${toolBtn} ${measureOn ? toolBtnActive : ''}`}
            onClick={toggleMeasure}
            disabled={status !== 'ready'}
            aria-pressed={measureOn}
          >
            Measure
          </button>
          <span className="self-center text-xs text-gray-500">
            Orbit · scroll zoom · drag pan
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        className={`${heightClass} w-full bg-[#e8e6e1] rounded-xl overflow-hidden`}
      />
    </div>
  );
}

export default Viewer;
